/**
 * Buscador unificado del ConceptoPicker (spec §5.1, P3a Task 4).
 *
 * Funciones PURAS (sin storage, sin UI): consumibles desde el picker y tests.
 * Cuatro fuentes, en orden de prioridad (la primera gana por concepto):
 *  1. Por concepto: containment BIDIRECCIONAL sobre el nombre normalizado
 *     ("flete" encuentra "Flete"; "flete alfalfa" ⊇ "flete" → coincidencia
 *     parcial con Detalle pre-llenado "alfalfa").
 *  2. Por categoría: query sobre categoria.nombre → se muestran los conceptos
 *     de esa categoría (la sección se ve agrupada bajo su header).
 *  3. Por detalle histórico: query ⊆ texto de movimientos pasados (columna
 *     `concepto` repropiada como detalle) → concepto linkeado con hint.
 *  4. Fuzzy (último recurso): términos ≥ 5 chars a distancia de edición ≤ 2
 *     de una palabra de nombre o detalle → badge "¿Quisiste decir?" (solo
 *     sugiere, nunca auto-asigna).
 */
import { Concepto, Categoria } from '../types/administracion';
import { normalizarTexto } from './conceptosHelper';

export interface MatchDeBusqueda {
  concepto: Concepto;
  /** Etiqueta informativa a la derecha de la opción (hint / coincidencia parcial) */
  etiqueta?: string;
  /** Texto para pre-llenar el campo Detalle del form (match parcial del nombre) */
  detallePrefill?: string;
  /** Sugerencia fuzzy: se renderiza con badge y al final de la lista */
  fuzzy?: boolean;
}

/** Distancia mínima de edición para que el fuzzy sugiera. */
export const MAX_DISTANCIA_EDICION = 2;
/** Largo mínimo de un término del query para participar del fuzzy. */
export const MIN_LEN_TERMINO_FUZZY = 5;

/**
 * Distancia de edición (Levenshtein) con dos filas.
 * "vamonde"/"bamonde" → 1; "fleto"/"flete" → 1.
 */
export function distanciaEdicion(a: string, b: string): number {
  if (a === b) return 0;
  const prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let diag = prev[0];
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const arriba = prev[j];
      prev[j] = Math.min(
        prev[j] + 1, // borrado
        prev[j - 1] + 1, // inserción
        diag + (a[i - 1] === b[j - 1] ? 0 : 1) // sustitución
      );
      diag = arriba;
    }
  }
  return prev[b.length];
}

/** ¿Algún término del query (≥ MIN_LEN) está a distancia ≤ MAX de una palabra del texto? */
function hayMatchFuzzy(terminos: string[], textoNormalizado: string): boolean {
  const palabras = textoNormalizado.split(' ').filter(Boolean);
  return terminos.some(t => palabras.some(p => distanciaEdicion(t, p) <= MAX_DISTANCIA_EDICION));
}

/**
 * Busca sobre las tres fuentes + fuzzy. Sin query devuelve todos los conceptos
 * (el picker los agrupa por categoría y ordena por frecuencia).
 *
 * `detallesPorConcepto` es el índice de textos históricos (columna `concepto`
 * de gastos/ingresos y `nombre` de stocks_historico) por concepto_id; los
 * textos iguales al nombre del concepto (movimientos sin detalle) se ignoran.
 */
export function buscarConceptos(
  query: string,
  conceptos: Concepto[],
  categorias: Categoria[],
  detallesPorConcepto: Record<string, string[]>
): MatchDeBusqueda[] {
  const q = normalizarTexto(query);
  if (!q) return conceptos.map(concepto => ({ concepto }));

  // Un concepto puede matchear por varias fuentes: gana la primera (prioridad)
  const matches = new Map<string, MatchDeBusqueda>();
  const add = (m: MatchDeBusqueda) => {
    if (!matches.has(m.concepto.id)) matches.set(m.concepto.id, m);
  };

  // 1. Por concepto: containment bidireccional sobre el nombre
  for (const concepto of conceptos) {
    const nombre = normalizarTexto(concepto.nombre);
    if (!nombre) continue;
    if (nombre.includes(q)) {
      add({ concepto });
    } else if (q.includes(nombre)) {
      // "flete alfalfa" ⊇ "flete": el resto del query pre-llena el Detalle
      const resto = q.replace(nombre, ' ').trim();
      add({
        concepto,
        etiqueta: '(coincidencia parcial)',
        ...(resto ? { detallePrefill: resto } : {}),
      });
    }
  }

  // 2. Por categoría: match del query sobre el nombre → sus conceptos
  for (const categoria of categorias) {
    const nombreCat = normalizarTexto(categoria.nombre);
    if (!nombreCat) continue;
    if (nombreCat.includes(q) || q.includes(nombreCat)) {
      for (const concepto of conceptos) {
        if (concepto.categoria_id === categoria.id) add({ concepto });
      }
    }
  }

  // 3. Por detalle histórico: query ⊆ texto del movimiento → concepto linkeado
  for (const concepto of conceptos) {
    const nombre = normalizarTexto(concepto.nombre);
    for (const texto of detallesPorConcepto[concepto.id] ?? []) {
      const t = normalizarTexto(texto);
      if (!t || t === nombre) continue; // movimiento sin detalle (texto = nombre)
      if (t.includes(q)) {
        add({ concepto, etiqueta: `detalle usado antes: «${texto}»` });
        break;
      }
    }
  }

  // 4. Fuzzy: último recurso, solo para conceptos sin match exacto/containment
  const terminos = q.split(' ').filter(t => t.length >= MIN_LEN_TERMINO_FUZZY);
  if (terminos.length > 0) {
    for (const concepto of conceptos) {
      if (matches.has(concepto.id)) continue;
      if (hayMatchFuzzy(terminos, normalizarTexto(concepto.nombre))) {
        add({ concepto, fuzzy: true });
        continue;
      }
      const nombre = normalizarTexto(concepto.nombre);
      for (const texto of detallesPorConcepto[concepto.id] ?? []) {
        const t = normalizarTexto(texto);
        if (!t || t === nombre) continue;
        if (hayMatchFuzzy(terminos, t)) {
          add({ concepto, fuzzy: true, etiqueta: `detalle usado antes: «${texto}»` });
          break;
        }
      }
    }
  }

  return [...matches.values()];
}
